import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { CompanyModel, getCompanies } from '../data/company';
import { sendFiles, sendCompanyRequest, getTaskStatus, createTaskEventSource, sendCandidateRequest } from '../data/analysis';
import { CandidateModel } from '../data/candidate';
import { analyseForCV } from '../data/analysis';

export interface AnalysisState {
    companies: {
        matchingCompanies: CompanyModel[];
        allCompanies: CompanyModel[];
        matchId: string;
        loading: 'idle' | 'pending' | 'succeeded' | 'failed';
        error?: string;
    };
    services: {
        serviceOfferings: string;
        loading: 'idle' | 'pending' | 'succeeded' | 'failed';
        progress: {
            taskId: string;
            progress: number;
            progressMessage: string;
        };
        error?: string;
    };
    profile: {
        result: string;
        loading: 'idle' | 'pending' | 'succeeded' | 'failed';
        error?: string;
    };
    candidates: {
        loading: 'idle' | 'pending' | 'succeeded' | 'failed';
        candidateFilterResults: CandidateModel;
        error?: string;
    }
}

const initialState: AnalysisState = {
    companies: {
        matchingCompanies: [],
        allCompanies: [],
        matchId: "",
        loading: 'idle',
        error: ""
    },
    services: {
        serviceOfferings: "",
        loading: 'idle',
        progress: {
            taskId: "",
            progress: 0,
            progressMessage: ""
        },
        error: ""
    },
    profile: {
        result: "",
        loading: 'idle',
        error: ""
    }, 
    candidates: {
        loading: 'idle',
        candidateFilterResults: {} as CandidateModel,
        error: ""
    }
};

const slice = createSlice({
    name: 'analysis',
    initialState,
    reducers: {
        resetAnalysis: (state) => {
            state.companies = {
                matchingCompanies: [],
                allCompanies: [],
                matchId: "",
                loading: 'idle',
                error: ""
            };
        
            state.services = {
                serviceOfferings: "",
                loading: 'idle',
                progress: {
                    taskId: "",
                    progress: 0,
                    progressMessage: ""
                },
                error: ""
            };

            state.profile = {
                result: "",
                loading: 'idle',
                error: ""
            };
        
            state.candidates = {
                loading: 'idle',
                candidateFilterResults: {} as CandidateModel,
                error: ""
            };
        },        
        setMatchCompanies: (state, action: PayloadAction<CompanyModel[]>) => {
            state.companies.matchingCompanies = action.payload;
        },
        setSearchCompanies: (state, action: PayloadAction<CompanyModel[]>) => {
            state.companies.allCompanies = action.payload;
        },
        setTaskId: (state, action: PayloadAction<string>) => {
            state.services.progress.taskId = action.payload;
        },
        setProgress: (state, action: PayloadAction<{progress: number, message: string}>) => {
            state.services.progress.progress = action.payload.progress;
            state.services.progress.progressMessage = action.payload.message;
        },
        setAnalysisResult: (state, action: PayloadAction<string>) => {
            state.services.loading = 'succeeded';
            state.services.serviceOfferings = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(matchCompanies.pending, (state) => {
                state.companies.loading = 'pending';
            })
            .addCase(matchCompanies.fulfilled, (state, action) => {
                state.companies.loading = 'succeeded';
                state.companies.matchingCompanies = action.payload.companies;
                state.companies.matchId = action.payload.matchId;
            })
            .addCase(matchCompanies.rejected, (state, action) => {
                state.companies.loading = 'failed';
                state.companies.error = action.error.message || 'Unknown error';
            })
            .addCase(retriveCompanies.pending, (state) => {
                state.companies.loading = 'pending';
            })
            .addCase(retriveCompanies.fulfilled, (state, action) => {
                state.companies.loading = 'succeeded';
                state.companies.allCompanies = action.payload
            })
            .addCase(retriveCompanies.rejected, (state, action) => {
                state.companies.loading = 'failed';
                state.companies.error = action.error.message || 'Unknown error';
            })
            .addCase(matchCandidates.pending, (state) => {
                state.candidates.loading = 'pending';
            })
            .addCase(matchCandidates.fulfilled, (state, action) => {
                state.candidates.loading = 'succeeded';
                state.candidates.candidateFilterResults = action.payload || {};
            })
            .addCase(matchCandidates.rejected, (state, action) => {
                state.candidates.loading = 'failed';
                state.candidates.error = action.error.message || 'Unknown error';
            })
            .addCase(analyseFiles.pending, (state) => {
                state.services.loading = 'pending';
                state.services.progress.progress = 0;
                state.services.progress.progressMessage = "Starting analysis...";
            })
            .addCase(analyseFiles.fulfilled, (state) => {
                if (state.services.progress.progress < 100) {
                    state.services.progress.progress = 100;
                    state.services.progress.progressMessage = "Analysis complete!";
                }
            })
            .addCase(analyseFiles.rejected, (state, action) => {
                state.services.loading = 'failed';
                state.services.error = action.error.message || 'Unknown error';
            })
            .addCase(analyseProfileRequirements.pending, (state) => {
                state.profile.loading = 'pending';
                state.services.progress.progress = 0;
                state.services.progress.progressMessage = "Starting analysis...";
            })
            .addCase(analyseProfileRequirements.fulfilled, (state, action) => {
                state.profile.result = action.payload
            })
            .addCase(analyseProfileRequirements.rejected, (state, action) => {
                state.profile.loading = 'failed';
                state.profile.error = action.error.message || 'Unknown error';
            });
    },
});

export const { 
    resetAnalysis, 
    setMatchCompanies, 
    setSearchCompanies,
    setTaskId, 
    setProgress, 
    setAnalysisResult 
} = slice.actions;

export default slice.reducer;

export const analyseFiles = createAsyncThunk(
    'analysis/project-analysis',
    async (
        { files, userPrompt }: { files: File[]; userPrompt: string },
        { dispatch, rejectWithValue }
    ) => {
        try {
            const formData = new FormData();
            files.forEach(file => {
                formData.append('files', file);
            });
            if (userPrompt !== '') formData.append('free_text_input', userPrompt);
            
            // Start the task and get task ID
            const taskId = await sendFiles(formData);
            dispatch(setTaskId(taskId));
            
            // Create event source for streaming updates
            const eventSource = createTaskEventSource(taskId);
            
            return new Promise<string>((resolve, reject) => {
                let result: string | null = null;
                let pollingActive = false;
                
                // Handle SSE messages
                eventSource.onmessage = (event) => {
                    const data = JSON.parse(event.data);
                    
                    // Update progress
                    dispatch(setProgress({
                        progress: data.progress,
                        message: data.message
                    }));
                    
                    // Task completed
                    if (data.status === 'completed') {
                        // Make sure result is not null before resolving
                        const analysisResult = data.result || ""; // Provide empty string as fallback
                        eventSource.close();
                        dispatch(setAnalysisResult(analysisResult));
                        resolve(analysisResult);
                    } 
                    // Task failed
                    else if (data.status === 'error') {
                        eventSource.close();
                        reject(new Error(data.message));
                    }
                };
                
                // Handle SSE errors - fall back to polling
                eventSource.onerror = () => {
                    eventSource.close();
                    var errorCount = 0;
                    // Only start polling if not already polling
                    if (!pollingActive && !result) {
                        pollingActive = true;
                        
                        // Start polling
                        const pollInterval = setInterval(async () => {
                            try {
                                const status = await getTaskStatus(taskId);
                                
                                // Update progress
                                dispatch(setProgress({
                                    progress: status.progress,
                                    message: status.message
                                }));
                                
                                // Task completed
                                if (status.status === 'completed') {
                                    clearInterval(pollInterval);
                                    dispatch(setAnalysisResult(status.result));
                                    resolve(status.result);
                                } 
                                // Task failed
                                else if (status.status === 'error') {
                                    clearInterval(pollInterval);
                                    reject(new Error(status.message));
                                }
                            } catch (error) {
                                // Handle error during polling
                                console.error('Polling error:', error);
                                // Optionally, you can retry the polling a few times before rejecting
                                errorCount++;
                                if (errorCount >= 3) {
                                    clearInterval(pollInterval);
                                    reject(new Error('Polling failed after multiple attempts'));
                                }
                            }
                        }, 2000); // Poll every 2 seconds
                    }
                };
            });
        } catch (err) {
            console.error('File analysis failed:', err);
            return rejectWithValue('Failed to analyse files');
        }
    }
);

export const matchCompanies = createAsyncThunk(
    'analysis/match-companies',
    async (serviceOfferings: string, { rejectWithValue }) => {
        try {
            const response = await sendCompanyRequest(serviceOfferings);

            const matchedCompanies: CompanyModel[] = response.matches.map((match: CompanyModel) => ({
                ...match,
                selected: true,
            }));
            /*const matchedCompanies = [
                  {
                    _id: "Dsdcs",
                    name: "ACerail",
                    website: "https://www.acerail.se/",
                    description: "ACerail is a Swedish consulting company specializing in infrastructure projects, including construction management and project management.",
                    matching_offerings: [
                      "Construction Management",
                      "Project Management",
                      "Infrastructure Solutions"
                    ],
                      "match_reason": "ACerail is a Swedish company with a strong focus on infrastructure projects, making it a suitable match for identifying construction companies in Sweden.",
                      "match_score": 0.9,
                      "selected": true
                  },
                  {
                    _id: "asd",
                    name: "Bro & Betong",
                    website: "https://brobetong.se/",
                    description: "Bro & Betong offers project and construction management services within infrastructure and urban development in Sweden.",
                    matching_offerings: [
                      "Project Management",
                      "Construction Management",
                      "Infrastructure Projects"
                    ],
                    match_reason: "Bro & Betong is a Swedish company that provides construction management services specifically within infrastructure, aligning well with the project requirements.",
                    match_score: 0.95,
                    selected: true
                  },
                  {
                    _id: "asd",
                    name: "Infra Action",
                    website: "https://www.infraaction.se/",
                    description: "Infra Action specializes in project and construction management services for infrastructure projects across Sweden.",
                    matching_offerings: [
                      "Project Management",
                      "Construction Management",
                      "Infrastructure"
                    ],
                    match_reason: "Infra Action is a Swedish company with a focus on infrastructure and construction management, making it a relevant choice for the project.",
                    match_score: 0.9,
                    selected: true
                  },
                  {
                    _id: "rfrfer",
                    name: "Toofab",
                    website: "https://toofab.se/",
                    description: "Toofab provides project management services for infrastructure and construction projects in Sweden.",
                    matching_offerings: [
                      "Project Management",
                      "Infrastructure Projects",
                      "Construction Management"
                    ],
                    match_reason: "Toofab is involved in infrastructure and construction projects in Sweden, aligning with the project requirements for identifying construction companies.",
                    match_score: 0.85,
                    selected: true
                  },
                  {
                    _id: "rerfer",
                    name: "Vibroakustik",
                    website: "https://vibroakustik.se/",
                    description: "Vibroakustik specializes in environmental impact assessments and noise and vibration analysis for construction projects in Sweden.",
                    matching_offerings: [
                      "Environmental Impact Assessments",
                      "Vibration and Noise Analysis"
                    ],
                    match_reason: "Vibroakustik provides services related to environmental assessments for construction projects in Sweden, which is relevant for infrastructure development.",
                    match_score: 0.8,
                    selected: true
                  }
            ];*/
            return { companies: matchedCompanies, matchId: response.match_id  };
        } catch (err) {
            console.error('Company matching failed:', err);
            return rejectWithValue('Failed to match companies');
        }
    }
);

export const matchCandidates = createAsyncThunk(
    'analysis/match-candidates',
    async (
        { decision_criteria, companies, match_id }: { decision_criteria: string; companies: CompanyModel[] | null; match_id: string },
        { rejectWithValue }
    ) => {
        try {
            const requestPayload = {
                decision_criteria: decision_criteria,
                match_id: match_id,
                pre_company_filter: companies?.map(company => company._id),
                threshold: 0.6
            };
            const response = await sendCandidateRequest(requestPayload);
            return response;
        } catch (err) {
            return rejectWithValue('Candidate matching failed');
        }
    }
);

export const analyseProfileRequirements = createAsyncThunk(
    'analysis/analyse-profile-requirements',
    async (
        { taskId, userPrompt }: { taskId: string; userPrompt?: string } , { rejectWithValue }) => {
        try {
            const result = await analyseForCV(taskId, userPrompt);
            return result.cv_analysis_result[0];
        } catch (err: any) {
            return rejectWithValue(err.message || 'CV analysis failed');
        }
    }
);

export const retriveCompanies = createAsyncThunk(
    'analysis/get-companies',
    async (_, { rejectWithValue }) => {
        try {
            const result = await getCompanies();
            const companies: CompanyModel[] = result.map((company: CompanyModel) => ({
                ...company,
                selected: false,
            }));
            return companies;
        } catch (err: any) {
            return rejectWithValue(err.message || 'Companies retrieval  failed');
        }
    }
);
